import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Users, Plus, Trash2, X, Search, ChevronRight, Lock } from 'lucide-react';

const COLORES_ROL = {
  coordinador_general:     'bg-purple-100 text-purple-800',
  coordinador_territorial: 'bg-blue-100 text-blue-800',
  coordinador_sectorial:   'bg-indigo-100 text-indigo-800',
  seccional:               'bg-green-100 text-green-800',
  operador:                'bg-yellow-100 text-yellow-800',
  promotor:                'bg-gray-100 text-gray-700',
};

const LABEL_ROL = {
  coordinador_general:     'Coord. General',
  coordinador_territorial: 'Coord. Territorial',
  coordinador_sectorial:   'Coord. Sectorial',
  seccional:               'Seccional',
  operador:                'Operador',
  promotor:                'Promotor',
};

const ORDEN_ROL = {
  coordinador_general: 1,
  coordinador_territorial: 2,
  coordinador_sectorial: 3,
  seccional: 4,
  operador: 5,
  promotor: 6,
};

export default function Asignaciones() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const puedeGestionar = ['superadmin', 'coordinador_general'].includes(user?.rol);

  const [usuarios, setUsuarios] = useState([]);
  const [liderSeleccionado, setLiderSeleccionado] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  const [candidatos, setCandidatos] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAsig, setLoadingAsig] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaCandidato, setBusquedaCandidato] = useState('');
  const [mensaje, setMensaje] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [seccionAsig, setSeccionAsig] = useState('');
  const [candidatoSeleccionado, setCandidatoSeleccionado] = useState(null);
  const [filtrRol, setFiltroRol] = useState('');

  useEffect(() => {
    if (puedeGestionar) fetchUsuarios();
    else setLoading(false);
  }, [puedeGestionar]);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const res = await api.get('/asignaciones/resumen');
      setUsuarios(res.data.usuarios || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const seleccionarLider = async (u) => {
    setLiderSeleccionado(u);
    setAsignaciones([]);
    setCandidatos([]);
    setBusquedaCandidato('');
    setLoadingAsig(true);
    try {
      const [asigRes, candRes] = await Promise.all([
        api.get(`/asignaciones/lider/${u.id}`),
        api.get(`/asignaciones/candidatos/${u.id}`)
      ]);
      setAsignaciones(asigRes.data.asignaciones || []);
      setCandidatos(candRes.data.candidatos || []);
      setSecciones(candRes.data.secciones || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingAsig(false);
    }
  };

  const handleAsignar = async () => {
    if (!candidatoSeleccionado) return;
    try {
      await api.post('/asignaciones', {
        lider_user_id: liderSeleccionado.id,
        subordinado_user_id: candidatoSeleccionado.id,
        seccion_id: seccionAsig || null
      });
      setMensaje({ tipo: 'exito', texto: `✅ ${candidatoSeleccionado.nombre} ${candidatoSeleccionado.apellido_paterno} asignado correctamente.` });
      setMostrarModal(false);
      setCandidatoSeleccionado(null);
      setSeccionAsig('');
      await seleccionarLider(liderSeleccionado);
      fetchUsuarios();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al asignar.' });
    }
  };

  const handleEliminarAsignacion = async (id, nombre) => {
    if (!window.confirm(`¿Quitar la asignación de ${nombre}?`)) return;
    try {
      await api.delete(`/asignaciones/${id}`);
      setMensaje({ tipo: 'exito', texto: 'Asignación eliminada.' });
      await seleccionarLider(liderSeleccionado);
      fetchUsuarios();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al eliminar.' });
    }
  };

  const usuariosFiltrados = usuarios
    .filter(u => {
      const nombre = `${u.nombre} ${u.apellido_paterno}`.toLowerCase();
      const matchBusqueda = nombre.includes(busqueda.toLowerCase());
      const matchRol = !filtrRol || u.rol === filtrRol;
      return matchBusqueda && matchRol && u.rol !== 'promotor'; // promotores no lideran a nadie
    })
    .sort((a, b) => (ORDEN_ROL[a.rol] || 9) - (ORDEN_ROL[b.rol] || 9));

  const candidatosFiltrados = candidatos.filter(c =>
    `${c.nombre} ${c.apellido_paterno}`.toLowerCase().includes(busquedaCandidato.toLowerCase())
  );

  if (!puedeGestionar) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            ← Volver
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="text-green-600" size={28} /> Asignaciones Jerárquicas
          </h2>
          <p className="text-gray-500 text-sm">Define quién supervisa a quién dentro de la estructura</p>
        </div>

        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>{mensaje.texto}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── PANEL IZQUIERDO — Lista de líderes ── */}
          <div>
            <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
              <p className="text-sm font-semibold text-gray-700 mb-3">Selecciona un usuario para ver y gestionar sus asignaciones</p>
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                  <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <select value={filtrRol} onChange={(e) => setFiltroRol(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Todos</option>
                  <option value="coordinador_general">Coord. General</option>
                  <option value="coordinador_territorial">Coord. Territorial</option>
                  <option value="coordinador_sectorial">Coord. Sectorial</option>
                  <option value="seccional">Seccional</option>
                  <option value="operador">Operador</option>
                </select>
              </div>
              {loading ? (
                <p className="text-center text-gray-400 py-4 text-sm">Cargando...</p>
              ) : usuariosFiltrados.length === 0 ? (
                <p className="text-center text-gray-400 py-4 text-sm">No se encontraron usuarios</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {usuariosFiltrados.map(u => (
                    <button key={u.id} onClick={() => seleccionarLider(u)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition text-left ${
                        liderSeleccionado?.id === u.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                      <div>
                        <p className="font-medium text-sm text-gray-800">{u.nombre} {u.apellido_paterno}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLORES_ROL[u.rol] || 'bg-gray-100 text-gray-700'}`}>
                            {LABEL_ROL[u.rol] || u.rol}
                          </span>
                          {u.numero_seccion && (
                            <span className="text-xs text-gray-500">Sección {u.numero_seccion}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {parseInt(u.total_subordinados) > 0 && (
                          <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                            {u.total_subordinados} asig.
                          </span>
                        )}
                        <ChevronRight size={16} className="text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── PANEL DERECHO — Asignaciones del líder seleccionado ── */}
          <div>
            {!liderSeleccionado ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Users className="text-gray-300 mx-auto mb-3" size={48} />
                <p className="text-gray-500">Selecciona un usuario de la izquierda para ver sus asignaciones</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-bold text-gray-800">{liderSeleccionado.nombre} {liderSeleccionado.apellido_paterno}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLORES_ROL[liderSeleccionado.rol] || 'bg-gray-100 text-gray-700'}`}>
                      {LABEL_ROL[liderSeleccionado.rol] || liderSeleccionado.rol}
                    </span>
                  </div>
                  {candidatos.length > 0 && (
                    <button onClick={() => { setCandidatoSeleccionado(null); setSeccionAsig(''); setBusquedaCandidato(''); setMostrarModal(true); }}
                      className="text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2 text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      <Plus size={16} /> Agregar
                    </button>
                  )}
                </div>

                {loadingAsig ? (
                  <p className="text-center text-gray-400 py-8 text-sm">Cargando asignaciones...</p>
                ) : asignaciones.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 text-sm mb-2">Sin asignaciones aún</p>
                    {candidatos.length > 0 ? (
                      <button onClick={() => { setCandidatoSeleccionado(null); setSeccionAsig(''); setBusquedaCandidato(''); setMostrarModal(true); }}
                        className="text-green-600 text-sm font-medium hover:underline">
                        + Agregar primer asignación
                      </button>
                    ) : (
                      <p className="text-xs text-gray-400">No hay usuarios disponibles para asignar a este rol</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[460px] overflow-y-auto">
                    {asignaciones.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                        <div>
                          <p className="font-medium text-sm text-gray-800">{a.subordinado_nombre} {a.subordinado_apellido}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLORES_ROL[a.subordinado_rol] || 'bg-gray-100 text-gray-700'}`}>
                              {LABEL_ROL[a.subordinado_rol] || a.subordinado_rol}
                            </span>
                            {a.numero_seccion && (
                              <span className="text-xs text-gray-500">Sección {a.numero_seccion}</span>
                            )}
                          </div>
                        </div>
                        <button onClick={() => handleEliminarAsignacion(a.id, `${a.subordinado_nombre} ${a.subordinado_apellido}`)}
                          className="p-1.5 hover:bg-red-100 rounded-lg transition">
                          <Trash2 size={15} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* ── MODAL AGREGAR ASIGNACIÓN ── */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <div>
                <h3 className="text-white font-bold text-lg">Agregar Asignación</h3>
                <p className="text-green-200 text-xs">Líder: {liderSeleccionado?.nombre} {liderSeleccionado?.apellido_paterno}</p>
              </div>
              <button onClick={() => setMostrarModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4 overflow-hidden flex-1">
              {/* Búsqueda de candidatos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecciona a quién asignar
                </label>
                <div className="relative mb-2">
                  <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                  <input value={busquedaCandidato} onChange={(e) => setBusquedaCandidato(e.target.value)}
                    placeholder="Buscar candidato..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto border border-gray-200 rounded-xl p-2">
                  {candidatosFiltrados.length === 0 ? (
                    <p className="text-center text-gray-400 py-4 text-sm">No hay candidatos disponibles</p>
                  ) : candidatosFiltrados.map(c => (
                    <button key={c.id} onClick={() => setCandidatoSeleccionado(c)}
                      className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition text-left ${
                        candidatoSeleccionado?.id === c.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                      <div>
                        <p className="font-medium text-sm">{c.nombre} {c.apellido_paterno}</p>
                        <div className="flex gap-2 mt-0.5">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${COLORES_ROL[c.rol] || 'bg-gray-100 text-gray-700'}`}>
                            {LABEL_ROL[c.rol] || c.rol}
                          </span>
                          {c.numero_seccion && <span className="text-xs text-gray-500">Secc. {c.numero_seccion}</span>}
                        </div>
                      </div>
                      {candidatoSeleccionado?.id === c.id && (
                        <span className="text-green-600 text-xs font-bold">✓ Seleccionado</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sección opcional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sección asociada <span className="text-gray-400 text-xs">(opcional)</span>
                </label>
                <select value={seccionAsig} onChange={(e) => setSeccionAsig(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Sin sección específica</option>
                  {secciones.map(s => (
                    <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 p-5 pt-0">
              <button onClick={() => setMostrarModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={handleAsignar} disabled={!candidatoSeleccionado}
                className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
